/**
 * ProfileHealthCard — dashboard card showing profile completeness
 * and actionable suggestions (PR-P8-2, Option D).
 *
 * Surfaces when the system detects gaps in the profile: missing
 * geographies, no AI systems, no compliance goals, stale profile
 * (not updated in 6+ months). Links to the specific Settings
 * sections that need attention.
 */

import type { ParsedCustomerProfile } from '@aigovops/core';
import { Link } from 'react-router-dom';

export interface ProfileGap {
  key: string;
  label: string;
  settingsSection: string;
}

export function computeProfileGaps(
  profile: ParsedCustomerProfile | null,
  systemCount: number
): ProfileGap[] {
  if (!profile) return [{ key: 'no-profile', label: 'Complete onboarding to get started', settingsSection: 'profile' }];
  const gaps: ProfileGap[] = [];

  if (!profile.geographies || profile.geographies.length === 0) {
    gaps.push({ key: 'geo', label: 'Add operating geographies to determine which regulations apply', settingsSection: 'profile' });
  }
  if (!profile.service_classifications || profile.service_classifications.length === 0) {
    gaps.push({ key: 'cls', label: 'Select AI classifications so your register is tailored', settingsSection: 'profile' });
  }
  if (systemCount === 0) {
    gaps.push({ key: 'systems', label: 'Add AI systems to track per-system maturity', settingsSection: 'profile' });
  }
  if (!profile.compliance_posture || profile.compliance_posture.length === 0) {
    gaps.push({ key: 'goals', label: 'Set compliance goals so scoring targets the right frameworks', settingsSection: 'profile' });
  }
  if (!profile.data_types || profile.data_types.length === 0) {
    gaps.push({ key: 'data', label: 'Add data types to sharpen priority scoring', settingsSection: 'profile' });
  }

  const updatedAt = (profile as unknown as { updated_at?: string }).updated_at
    ? new Date((profile as unknown as { updated_at?: string }).updated_at!)
    : null;
  if (updatedAt) {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    if (updatedAt < sixMonthsAgo) {
      gaps.push({ key: 'stale', label: 'Review profile accuracy: six-month review due', settingsSection: 'profile' });
    }
  }

  return gaps;
}

export function ProfileHealthCard({
  gaps,
  workspaceSlug,
}: {
  gaps: ProfileGap[];
  workspaceSlug: string;
}) {
  if (gaps.length === 0) return null;

  return (
    <div
      style={{
        padding: 'var(--space-4)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-3)',
        }}
      >
        <div style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)' }}>
          Profile health: {gaps.length} suggestion{gaps.length > 1 ? 's' : ''}
        </div>
        <Link
          to={`/w/${workspaceSlug}/app/settings/profile`}
          style={{
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--accent)',
            textDecoration: 'none',
          }}
        >
          Edit profile
        </Link>
      </div>
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'grid',
          gap: 'var(--space-2)',
        }}
      >
        {gaps.map((gap) => (
          <li
            key={gap.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-muted)',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'var(--warning)',
                flexShrink: 0,
              }}
            />
            {gap.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
